import { useMutation, useQuery } from '@apollo/client';
import { ButtonSaveType } from '@components/ButtonSaveSplit';
import { confirm } from '@components/ConfirmModal/confirm';
import DashedDivider from '@components/Divider/DashedDivider';
import { FlexRow } from '@components/FlexRow';
import FormContainer from '@components/FormContainer';
import Icons from '@components/Icons';
import InputDate from '@components/InputDate';
import InputDocuments from '@components/InputDocuments';
import InputFieldControlled from '@components/InputFieldControlled';
import InputMultiSelect from '@components/InputMultiSelect';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import Text from '@components/Text';
import { isEmpty } from '@helper/validation.utils';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Box, Checkbox, IconButton, Menu, MenuItem, Stack, Typography, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { moment } from '@utils/date.utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ExpandableText } from '../ExpandableText';
import { SectionCard } from '../SectionCard';
import { SectionTitle } from '../SectionTitle';
import EmptyEducationIllustration from "./empty-education.svg";

import AddActionButton from '@components/AddActionButton';
import { ADD_DIPLOMA_TALENT, DELETE_DIPLOMA_TALENT, UPDATE_DIPLOMA_TALENT } from '@gql/diploma';
import { GET_OPTIONS_TALENT } from '@gql/options';
import { GET_TALENT } from '@gql/talents';
import { checkPropertiesIsHaveValueWithIncludes } from '@helper/validation.utils';

export enum Degree {
    NO_DIPLOMA = `"C'est pas les diplômes qui comptent !"`,
    CAP_BEP = "CAP / BEP",
    BP = "Baccalauréat Professionnel",
    BAC_1 = "Baccalauréat",
    BAC_2 = "BTS, DEUG, DUT, DEUST (Bac+2)",
    BAC_3 = "Licence, Licence Pro, BUT (Bac+3)",
    BAC_4 = "Maîtrise, Master 1 (Bac+4)",
    BAC_5 = "Master, DESS, Ingénieur (Bac+5)",
    BAC_6 = "BAC+6",
    DOCTORATE = "Doctorat",
    CHERCHEUR = "Chercheur",
    ADDITIONAL_MENTION = "Mention complémentaire",
    // Old Version
    // TODO: Remove this after migration db
    OLD_DNB = "DNB",
    OLD_BACCALAUREAT_BP = "Baccalauréat, BP",
    OLD_DOCTORATE_BAC_5 = "Doctorat (>Bac+5)",
}

// Styled Components
const CheckboxContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
}));

const EducationItemContainer = styled(Box)(({ theme }) => ({
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    width: '100%',
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
    '&.error': {
        backgroundColor: theme.palette.errorRed?.main ? `${theme.palette.errorRed.main}20` : '#FCEEEE',
    },
    "&:first-child": {
        paddingTop: theme.spacing(0),
    },
    "&:last-child": {
        paddingBottom: theme.spacing(0),
    },
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
}));

const ErrorMessageContainer = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.palette.errorRed?.main ? `${theme.palette.errorRed.main}20` : '#FCEEEE',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
}));

type Props = {
    diploma?: any;
    onCancel?: () => void;
    onSubmitted?: () => void;
    isEdit?: boolean;
    talentId?: string;
};

const TalentEducationForm = ({ onCancel, onSubmitted, talentId, ...props }: Props) => {
    const [loading, setLoading] = useState(false);
    const [formState, setFormState] = useState<'view' | 'edit'>('edit');
    const [saveType, setSaveType] = useState<ButtonSaveType>(ButtonSaveType.SAVE_AND_RETURN);
    const [diploma, setDiploma] = useState<any>(props.diploma);
    const [randomKey, setRandomKey] = useState(Math.random());
    const [currentlyStudying, setCurrentlyStudying] = useState(diploma?.endDate ? false : true);

    const defaultValues = useMemo(() => {
        return {
            school: diploma?.school?.name ?? null,
            title: diploma?.title ?? null,
            city: diploma?.city ?? null,
            type: diploma?.type ?? null,
            result: diploma?.result ?? null,
            profession: diploma?.profession?._id ?? null,
            startDate: diploma?.startDate ? moment(diploma?.startDate).format('YYYY-MM-DD') : null,
            endDate: diploma?.endDate ? moment(diploma?.endDate).format('YYYY-MM-DD') : null,
            description: diploma?.description ?? null,
            file: null,
        };
    }, [diploma]);

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        watch,
        clearErrors,
        setError,
    } = useForm({
        mode: 'all',
        defaultValues,
    });

    const [save] = useMutation(ADD_DIPLOMA_TALENT);
    const [update] = useMutation(UPDATE_DIPLOMA_TALENT);

    const { showSnackbar } = useSnackbar();

    const submitHandler = async diplomaInput => {
        setLoading(true);
        if (!diploma?._id) {
            await save({
                variables: {
                    diplomaInput,
                    talentId
                },
                refetchQueries: () => [
                    {
                        query: GET_TALENT,
                        variables: {
                            talentId
                        },
                    },
                ],
            });
            showSnackbar('Formation ajoutée avec succès', 'success');
        } else {
            await update({
                variables: {
                    _id: diploma?._id,
                    diplomaInput,
                    talentId
                },
                refetchQueries: () => [
                    {
                        query: GET_TALENT,
                        variables: {
                            talentId
                        },
                    },
                ],
            });
            showSnackbar('Formation modifiée avec succès', 'success');
        }

        if (saveType === ButtonSaveType.SAVE_AND_RETURN) {
            onSubmitted();
        } else {
            Object.keys(defaultValues).forEach(key => {
                setValue(key as keyof typeof defaultValues, null);
            });
            setDiploma(null);
            setRandomKey(Math.random());
        }
        setLoading(false);
    };

    const { data: optionsData } = useQuery(GET_OPTIONS_TALENT);

    const options = useMemo(() => {
        if (!optionsData?.options) return null;
        return optionsData.options;
    }, [optionsData]);

    return (
        <FormContainer
            onBack={onCancel}
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            onCancel={onCancel}
            onSubmit={handleSubmit(submitHandler)}
            onButtonSplitChange={type => {
                setSaveType(type);
            }}
            buttonSplitText={{
                [ButtonSaveType.SAVE_AND_RETURN]: 'Enregistrer',
                [ButtonSaveType.SAVE_AND_ADD]: 'Ajouter une nouvelle formation',
            }}
            submitText={props.isEdit ? 'Enregistrer' : 'Suivant'}
            formTitle={formState === 'view' ? 'Détails de la formation' : diploma ? 'Modifier ma formation' : 'Ajouter une formation'}
            formDescription={'Ajoutez les différentes formations que vous avez suivi '}>
            <InputFieldControlled name="school" control={control} label="École" placeholder="ex : Hairstylist" required />
            <InputFieldControlled name="title" control={control} label="Titre du diplôme" placeholder="ex : Coiffure" required />
            <InputFieldControlled name="city" control={control} label="Ville" placeholder="Ville" required />
            <Stack
                flexDirection={{
                    xs: 'column',
                    sm: 'row',
                }}
                gap={2}>
                <InputMultiSelect
                    groupedOptions={options?.degree?.map(item => {
                        return {
                            label: item.name,
                            options: item.children
                                ?.filter(item => item.name !== Degree.NO_DIPLOMA)
                                ?.map(child => ({
                                    ...child,
                                    label: child.name,
                                    value: child.id,
                                })),
                        };
                    })}
                    single
                    name="type"
                    control={control}
                    label="Diplôme"
                    placeholder="Diplôme"
                    required
                />
                <InputMultiSelect
                    groupedOptions={options?.diplomaScore?.map(item => {
                        return {
                            label: item.name,
                            options: item.children?.map(child => ({
                                ...child,
                                label: child.name,
                                value: child.id,
                            })),
                        };
                    })}
                    single
                    name="result"
                    control={control}
                    label="Résultat"
                    placeholder="Résultat"
                    required
                />
            </Stack>
            <FlexRow gap={2} alignItems={'flex-start'}>
                <InputDate
                    name="startDate"
                    control={control}
                    label="Date de début"
                    placeholder="Date de début"
                    onChange={val => {
                        if (
                            moment(val).isAfter(moment(getValues('endDate'))) ||
                            moment(val).isSame(moment(getValues('endDate')))
                        ) {
                            setValue('endDate', moment(val).add(1, 'day').format('YYYY-MM-DD'));
                        }
                    }}
                    required
                />
                <InputDate
                    name="endDate"
                    control={control}
                    minDate={watch('startDate') ? moment(watch('startDate')).add('1', 'day') : undefined}
                    label="Date de fin"
                    placeholder="Date de fin"
                    onChange={(val: string) => {
                        setCurrentlyStudying(val ? false : true);
                    }}
                    required={!currentlyStudying}
                />
            </FlexRow>
            {/* CHECKBOX CURRENTLY STUDYING */}
            <CheckboxContainer component="label">
                <Checkbox
                    id="checkbox-currently-studying"
                    checked={currentlyStudying}
                    onChange={e => {
                        if (e.target.checked) {
                            setValue('endDate', null);
                            clearErrors('endDate');
                        } else {
                            setError('endDate', {
                                type: 'required',
                                message: 'La date de fin est obligatoire',
                            });
                        }
                        setCurrentlyStudying(!!e.target.checked);
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                />
                <Text darkGray size={16} error={isEmpty(watch('endDate')) && !currentlyStudying}>
                    Formation en cours
                </Text>
            </CheckboxContainer>
            <InputFieldControlled
                name="description"
                control={control}
                multiline
                maxLength={1000}
                numberOfLines={5}
                label="Description"
                placeholder="Description"
            />
            <InputDocuments
                key={randomKey}
                name="file"
                control={control}
                label="Photocopie des diplômes"
                placeholder="Files"
                file={diploma?.file}
            />
        </FormContainer>
    );
};

export const TalentEducationItem = ({
    diploma,
    onDelete,
    onClick,
    divider = true,
    isDeleting = false,
}: {
    diploma: any;
    onDelete?: () => void;
    onClick?: () => void;
    divider?: boolean;
    isDeleting?: boolean;
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
    const theme = useTheme();

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        onClick?.();
    };

    const handleDelete = () => {
        handleMenuClose();
        onDelete?.();
    };

    const hasError = !checkTalentEducationValue(diploma).status;
    const location = [diploma?.city, diploma?.school?.name].filter(Boolean).join(', ');

    return (
        <>
            <EducationItemContainer
                className={hasError ? 'error' : ''}
            >
                {/* School Logo */}
                <Box
                    sx={{
                        width: 46,
                        height: 46,
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexShrink: 0,
                    }}>
                    <img
                        src={diploma?.school?.logo || '/icons/default-school-logo.jpg'}
                        alt="logo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </Box>

                {/* Education Content */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(1.5),
                    minWidth: 0, // Allows text to truncate properly
                }}>
                    {/* Header Section */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: '18px',
                                color: theme.palette.text.primary,
                                lineHeight: 1.2,
                            }}>
                            {diploma?.title}
                        </Typography>

                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: isMobile ? 'wrap' : 'nowrap',
                            gap: theme.spacing(1),
                        }}>
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    color: theme.palette.text.primary,
                                }}>
                                {location}
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '16px',
                                    color: theme.palette.text.primary,
                                    flexShrink: 0,
                                }}>
                                {diploma?.startDate && diploma?.endDate ? (
                                    `${moment(diploma.startDate).format('YYYY')} - ${moment(diploma.endDate).format('YYYY')}`
                                ) : (
                                    'Dates non renseignées'
                                )}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Result */}
                    <Typography
                        sx={{
                            fontWeight: 400,
                            fontSize: '16px',
                            color: theme.palette.text.secondary,
                        }}>
                        {diploma?.result && `Niveau : ${diploma.result}`}
                    </Typography>

                    {/* Description */}
                    {diploma?.description && (
                        <ExpandableText
                            content={diploma.description}
                            maxLine={2}
                        />
                    )}
                </Box>

                {/* Action Menu */}
                {onClick && onDelete && (
                    <ActionButtonsContainer>
                        <IconButton
                            disabled={isDeleting}
                            aria-label="more"
                            aria-controls={open ? 'action-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleMenuOpen}
                            size="small"
                            sx={{
                                borderRadius: '4px',
                                color: (theme) => theme.palette.text.secondary,
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.action.hover,
                                },
                            }}>
                            <MoreVertIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}>
                            <MenuItem onClick={handleEdit}>
                                <Icons size={16} name="edit" style={{ marginRight: 8 }} />
                                Modifier
                            </MenuItem>
                            <MenuItem onClick={handleDelete} sx={{ color: theme.palette.errorRed.main }}>
                                <Icons size={16} name="delete" style={{ marginRight: 8 }} />
                                Supprimer
                            </MenuItem>
                        </Menu>
                    </ActionButtonsContainer>
                )}
            </EducationItemContainer>
            {divider && <DashedDivider />}
        </>
    );
};

export const TalentEducationEmpty = () => {
    const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
    return (
        <Stack p={3} gap={3} justifyContent={'center'} alignItems={'center'}>
            <img src={EmptyEducationIllustration} alt="Aucun résultat trouvé" />
            <Stack maxWidth={435} gap={1}>
                <Typography textAlign="center">
                    Dites-nous quel est votre parcours
                </Typography>
                <Typography textAlign="center" color="textSecondary" fontSize={isMobile ? 12 : 16}>
                    Faites passer votre profil au niveau supérieur en incluant votre expérience professionnelle.
                </Typography>
            </Stack>
        </Stack>
    );
};

export const checkTalentEducationValue = education => {
    if (!education.current) {
        return checkPropertiesIsHaveValueWithIncludes(education, [
            'school',
            'title',
            'city',
            'type',
            'result',
            'startDate',
            'endDate',
        ]);
    }
    return checkPropertiesIsHaveValueWithIncludes(education, [
        'school',
        'title',
        'city',
        'type',
        'result',
        'startDate',
    ]);
};

const TalentEducationList = ({ talent }) => {
    const [selectedDiploma, setSelectedDiploma] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [deleteDiploma] = useMutation(DELETE_DIPLOMA_TALENT, {});
    const theme = useTheme();

    useEffect(() => {
        setHasError(
            talent?.diplomas?.some((diploma: any) => {
                return !checkTalentEducationValue(diploma).status;
            }),
        );
    }, [talent?.diplomas]);

    const handleDelete = useCallback((_id: string) => {
        (async () => {
            if (await confirm({ confirmation: 'Êtes-vous sûr de vouloir supprimer cette formation ?' })) {
                try {
                    setDeletingId(_id);
                    deleteDiploma({
                        variables: {
                            _id,
                            talentId: talent?._id,
                        },
                        refetchQueries: () => [
                            {
                                query: GET_TALENT,
                                variables: {
                                    talentId: talent?._id,
                                },
                            },
                        ],
                    });
                } finally {
                    setDeletingId(null);
                }
            } else {
                console.log('cancel cancelled');
            }
        })();
    }, [deleteDiploma, talent?._id]);


    if (openForm) {
        return <TalentEducationForm
            isEdit
            talentId={talent?._id}
            diploma={selectedDiploma}
            onCancel={() => {
                setOpenForm(false);
                setSelectedDiploma(null);
            }}
            onSubmitted={() => {
                setOpenForm(false);
                setSelectedDiploma(null);
            }}
        />
    }

    return (
        <SectionCard sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <FlexRow gap={1} alignItems="center" justifyContent="space-between">
                <SectionTitle title="Formation" withInfo />
                <AddActionButton
                    onClick={() => {
                        setOpenForm(true);
                        setSelectedDiploma(null);
                    }}
                />
            </FlexRow>
            {hasError && (
                <ErrorMessageContainer>
                    <Text size={14} sx={{ color: theme.palette.errorRed.main }}>
                        Veuillez compléter les informations manquantes ou supprimer la formation encadrée de
                        rouge.
                    </Text>
                </ErrorMessageContainer>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                {talent?.diplomas?.map((diploma: any, index: number) => (
                    <TalentEducationItem
                        key={diploma?._id}
                        onClick={() => {
                            setOpenForm(true);
                            setSelectedDiploma(diploma);
                        }}
                        onDelete={() => handleDelete(diploma?._id)}
                        diploma={diploma}
                        divider={index !== talent?.diplomas?.length - 1}
                        isDeleting={deletingId === diploma?._id}
                    />
                ))}
            </Box>
            {(talent?.diplomas?.length === 0 || !talent) && <TalentEducationEmpty />}
        </SectionCard>

    );
};

export default TalentEducationList;
